import { useEffect, useRef } from "react";
import { Navigate, useLocation, useParams } from "react-router-dom";
import { useWindowSize } from "usehooks-ts";
import {
  PageContainer,
  PageTitle,
} from "@volca/ui/components/ui/page-components";
import { Skeleton } from "@volca/ui/components/ui/skeleton";
import { Stack } from "@volca/ui/components/ui/stack";
import { cn } from "@volca/ui/lib/utils";
import { useBreadcrumbs } from "@/app-shell";
import { useNavColumn } from "@/app-shell/nav-column-state";
import { MAIN_CONTENT_ID } from "@/app-shell/skip-link";
import { canManageReport } from "@/profitability/access";
import {
  PROFITABILITY_PATH,
  profitabilityTemplatePath,
} from "@/profitability/routes";
import { SETTINGS_CONTROLS } from "@/profitability/settings/control-classes";
import { useTemplateSettings } from "@/profitability/settings/query";
import {
  legacySettingsPage,
  SettingsNav,
  settingsPath,
  visibleSettingsPages,
} from "./settings-tab/settings-nav";
import { SettingsTab } from "./settings-tab/settings-tab";
import { TemplateArchivedNotice, TemplateLoadNotice } from "./template-notices";
import { useTemplateReport } from "./use-template-report";

export const TemplateSettingsPage = ({
  businessId,
  reportId,
}: {
  businessId: string;
  reportId: string;
}) => {
  const { report, query } = useTemplateReport(businessId, reportId);
  const { settingsSection, ruleId } = useParams();
  const { pathname, search } = useLocation();
  const content = useRef<HTMLDivElement>(null);
  const settings = useTemplateSettings(
    businessId,
    reportId,
    !!report && !!ruleId,
  );
  const ruleLabel =
    settings.data?.rules?.find((rule) => rule.id === ruleId)?.label ?? "Rule";
  const ready = !!report && (!ruleId || !!settings.data || settings.isError);
  useEffect(() => {
    if (!ready) return;
    const target =
      content.current?.querySelector<HTMLElement>("h1") ??
      document.getElementById(MAIN_CONTENT_ID);
    target?.focus({ preventScroll: true });
    content.current?.scrollIntoView({ block: "start" });
  }, [pathname, ready]);
  const manages = !!report && canManageReport(report);
  const pages = visibleSettingsPages(manages);
  const selected =
    pages.find(
      (page) =>
        page.id ===
        (legacySettingsPage[settingsSection ?? ""] ?? settingsSection),
    ) ?? pages[0];
  const { width = 0 } = useWindowSize();
  const nav = useNavColumn();
  const docked =
    width >= 64 + (nav.hidden ? 0 : nav.width.value) + 240 + 400 + 64;
  useBreadcrumbs(
    [
      { label: "Job Costing", href: PROFITABILITY_PATH },
      {
        label: report?.name ?? "Agent",
        href: profitabilityTemplatePath(reportId),
      },
      { label: "Settings" },
      ...(selected.id === "general" ? [] : [{ label: selected.label }]),
      ...(ruleId ? [{ label: ruleLabel }] : []),
    ],
    { visible: false },
  );
  if (report && ruleId && selected.id !== "rules")
    return (
      <Navigate
        replace
        to={{ pathname: settingsPath(reportId, selected.id), search }}
      />
    );
  if (report && settingsSection && selected.id !== settingsSection)
    return (
      <Navigate
        replace
        to={{ pathname: settingsPath(reportId, selected.id), search }}
      />
    );
  return (
    <PageContainer
      ref={content}
      className={cn("max-w-3xl gap-8 py-10", SETTINGS_CONTROLS)}
    >
      <SettingsNav
        reportId={reportId}
        name={report?.name ?? "Agent"}
        page={selected.id}
        manages={manages}
        docked={docked}
      />
      {!ruleId && (
        <PageTitle tabIndex={-1} className="text-2xl">
          {selected.label}
        </PageTitle>
      )}
      {query.isPending && !report ? (
        <Stack gap={6} align="stretch" aria-busy role="status">
          <span className="sr-only">Loading agent</span>
          {["report", "period", "rules"].map((group) => (
            <Skeleton
              key={group}
              className="h-28 w-full rounded-xl"
              aria-hidden
            />
          ))}
        </Stack>
      ) : (
        <TemplateLoadNotice reports={query} found={!!report} />
      )}
      {report && (
        <>
          <TemplateArchivedNotice businessId={businessId} report={report} />
          <SettingsTab
            key={selected.id}
            businessId={businessId}
            report={report}
            page={selected.id}
            ruleId={ruleId}
          />
        </>
      )}
    </PageContainer>
  );
};
